import {
  AGE_GROUP_LABELS,
  AGE_GROUPS,
  type AgeGroup,
  type InvitationDto,
  type Role,
} from '@bjj/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/client';
import { useApiMutation } from '../api/hooks';
import { DataTable } from './DataTable';
import { Badge, Card, ConfirmButton, ErrorMessage, Field, formatDate } from './ui';

const INVITATION_KEYS = [
  ['admin', 'invitations'],
  ['admin', 'overview'],
];

export function InviteForm({
  role,
  onDone,
}: {
  role: 'student' | 'instructor';
  onDone?: () => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [ageGroup, setAgeGroup] = useState<AgeGroup>('adults');
  const [created, setCreated] = useState<InvitationDto | null>(null);
  const invite = useApiMutation<void, InvitationDto>(
    () => ({
      path: '/admin/invitations',
      method: 'POST',
      body: { email, name, role, ...(role === 'student' ? { ageGroup } : {}) },
    }),
    INVITATION_KEYS,
  );
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        invite.mutate(undefined, {
          onSuccess: (inv) => {
            setCreated(inv);
            setEmail('');
            setName('');
            onDone?.();
          },
        });
      }}
    >
      <ErrorMessage error={invite.error} />
      {created ? (
        <div className="alert success" role="status">
          Invitation created for {created.email}.{' '}
          {created.emailSent
            ? 'An email was sent.'
            : 'Email is not configured — share this single-use link securely:'}
          <div className="mono" data-testid="invite-url" style={{ marginTop: 6 }}>
            {created.inviteUrl}
          </div>
        </div>
      ) : null}
      <div className="form-row">
        <Field label="Full name">
          {(id) => (
            <input
              id={id}
              type="text"
              required
              maxLength={80}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <Field label="Email">
          {(id) => (
            <input
              id={id}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>
        {role === 'student' ? (
          <Field label="Group" hint="Kids and adults have separate curricula and leaderboards.">
            {(id) => (
              <select
                id={id}
                value={ageGroup}
                onChange={(e) => setAgeGroup(e.target.value as AgeGroup)}
              >
                {AGE_GROUPS.map((g) => (
                  <option key={g} value={g}>
                    {AGE_GROUP_LABELS[g]}
                  </option>
                ))}
              </select>
            )}
          </Field>
        ) : null}
      </div>
      <button className="btn primary" type="submit" disabled={invite.isPending}>
        {invite.isPending
          ? 'Sending…'
          : role === 'student'
            ? 'Invite student'
            : 'Invite instructor'}
      </button>
    </form>
  );
}

/**
 * Invitations for `roles` that nobody has accepted yet (pending or expired), with Resend and
 * Revoke. Shows nothing when there are none; accepted invitations appear as people in the list.
 */
export function PendingInvitations({ roles }: { roles: Role[] }) {
  const [shared, setShared] = useState<InvitationDto | null>(null);
  const list = useQuery({
    queryKey: ['admin', 'invitations', 'all'],
    queryFn: () =>
      api.request<{ items: InvitationDto[] }>('/admin/invitations?status=all').then((r) => r.items),
  });
  const resend = useApiMutation<string, InvitationDto>(
    (id) => ({ path: `/admin/invitations/${id}/resend`, method: 'POST', body: {} }),
    INVITATION_KEYS,
  );
  const revoke = useApiMutation<string>(
    (id) => ({ path: `/admin/invitations/${id}`, method: 'DELETE' }),
    INVITATION_KEYS,
  );
  const rows = list.data?.filter(
    (i) => roles.includes(i.role) && (i.status === 'pending' || i.status === 'expired'),
  );
  const error = list.error ?? resend.error ?? revoke.error;
  if (!rows?.length && !shared && !error) return null;
  return (
    <Card title="Pending invitations">
      <ErrorMessage error={error} />
      {shared ? (
        <div className="alert success" role="status">
          New link for {shared.email}{' '}
          {shared.emailSent ? '(emailed)' : '(email not configured — share securely)'}:
          <div className="mono">{shared.inviteUrl}</div>
        </div>
      ) : null}
      <DataTable
        rows={rows}
        rowKey={(i) => i.id}
        empty="No pending invitations."
        columns={[
          { key: 'name', header: 'Name', render: (i) => i.name },
          { key: 'email', header: 'Email', render: (i) => i.email },
          ...(roles.includes('student')
            ? [
                {
                  key: 'group',
                  header: 'Group',
                  render: (i: InvitationDto) => (i.ageGroup ? AGE_GROUP_LABELS[i.ageGroup] : '—'),
                },
              ]
            : []),
          ...(roles.length > 1
            ? [{ key: 'role', header: 'Role', render: (i: InvitationDto) => i.role }]
            : []),
          { key: 'status', header: 'Status', render: (i) => <Badge value={i.status} /> },
          { key: 'expires', header: 'Expires', render: (i) => formatDate(i.expiresAt) },
          { key: 'by', header: 'Invited by', render: (i) => i.invitedBy?.name ?? 'System' },
          {
            key: 'actions',
            header: '',
            render: (i) => (
              <div className="btn-row">
                <button
                  type="button"
                  className="btn small"
                  onClick={() => resend.mutate(i.id, { onSuccess: setShared })}
                >
                  Resend
                </button>
                {i.status === 'pending' ? (
                  <ConfirmButton onConfirm={() => revoke.mutate(i.id)}>Revoke</ConfirmButton>
                ) : null}
              </div>
            ),
          },
        ]}
      />
    </Card>
  );
}
